/**
 * Scripts/Core/ui-utils.ts
 * Utilidades globales de UI para la promoción.
 */

import { ButtonVariant } from "../Components/button";

export type MessageType = "success" | "error" | "warning" | "info";

export interface ExtraButton {
  text: string;
  variant?: ButtonVariant;
  onClick?: () => void;
  onRedirect?: string;
}

export interface MessageOptions {
  type?: MessageType;
  extraButtons?: ExtraButton[];
}

/**
 * Muestra un mensaje estilizado utilizando el componente modal global 'messageModal'.
 * @param title Título del mensaje.
 * @param body Cuerpo del mensaje.
 * @param type Tipo de mensaje para el icono (success, error, etc.)
 * @param options Opciones adicionales con botones extra
 */
export const showMessage = (
  title: string,
  body: string,
  type: MessageType = "success",
  options?: MessageOptions,
) => {
  const modal = document.querySelector(
    'app-modal[modal-id="messageModal"]',
  ) as any;
  const titleEl = document.getElementById("messageModalTitle");
  const bodyEl = document.getElementById("messageModalBody");
  const iconEl = document.getElementById("messageModalIcon");
  const footerEl = document.querySelector("#messageModalFooter") as HTMLElement;

  if (modal && titleEl && bodyEl && iconEl) {
    titleEl.innerText = title;
    bodyEl.innerText = body;

    // Inyectar icono basado en el tipo
    iconEl.innerHTML = getIconHtml(type);

    // Construir footer con "Aceptar" + botones extra
    if (footerEl && options?.extraButtons && options.extraButtons.length > 0) {
      let html =
        '<app-button data-confirm-modal data-close-modal text="Aceptar" variant="dialog" class="md:[&>button]:px-16"></app-button>';
      for (const btn of options.extraButtons) {
        const redirectAttr = btn.onRedirect
          ? ` data-redirect="${btn.onRedirect}"`
          : "";
        const variantAttr = btn.variant || "primary";
        html += `<app-button class="extra-message-btn" data-extra-btn text="${btn.text}" variant="${variantAttr}" class="[&>button]:px-16"${redirectAttr}></app-button>`;
      }
      footerEl.innerHTML = `<div class="px-6 pb-8 flex justify-center items-center gap-4">${html}</div>`;
    } else if (footerEl) {
      footerEl.innerHTML =
        '<div class="px-6 pb-8 flex justify-center"><app-button data-confirm-modal data-close-modal text="Aceptar" variant="dialog" class="[&>button]:px-16"></app-button></div>';
    }

    // Adjuntar listeners a botones extra
    if (options?.extraButtons) {
      const extraBtns = footerEl?.querySelectorAll("[data-extra-btn]");
      extraBtns?.forEach((el: Element, index: number) => {
        const btnOptions = options.extraButtons![index];
        el.addEventListener("click", () => {
          if (btnOptions.onClick) {
            btnOptions.onClick();
          }
          if (btnOptions.onRedirect) {
            window.location.href = btnOptions.onRedirect;
          } else {
            modal.close();
          }
        });
      });
    }

    modal.open();
  } else {
    // Fallback nativo
    alert(`${title}: ${body}`);
  }
};

/**
 * Genera el HTML del icono basado en el tipo.
 */
function getIconHtml(type: MessageType): string {
  switch (type) {
    case "success":
      return `
                <div class="bg-green-100 p-4 rounded-full shadow-inner">
                    <svg class="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>`;
    case "error":
      return `
                <div class="bg-red-100 p-4 rounded-full shadow-inner">
                    <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </div>`;
    case "warning":
      return `
                <div class="bg-amber-100 p-4 rounded-full shadow-inner">
                    <svg class="w-12 h-12 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                </div>`;
    default: // info
      return `
                <div class="bg-blue-100 p-4 rounded-full shadow-inner">
                    <svg class="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                </div>`;
  }
}
